import { setTimeout as delay } from 'node:timers/promises';

import { log } from './logger.ts';

export const DEFAULT_FETCH_RETRY_LIMIT = 5;
export const DEFAULT_FETCH_RETRY_BASE_MS = 1000;
export const DEFAULT_FETCH_RETRY_MAX_MS = 16_000;

export type FetchRetryOptions = {
  limit?: number;
  baseMs?: number;
  maxMs?: number;
  sleep?: (ms: number) => Promise<void>;
  isRetryableError: (error: unknown) => boolean;
  describeRetryReason?: (error: unknown) => string;
  logEvent?: string;
  logFields?: Record<string, string | number | boolean>;
};

// Single retry loop shared by every fetch-style call site. Callers control
// what counts as retryable via `isRetryableError`; the loop owns backoff,
// attempt accounting, and structured logging. Response status is never
// retried here — non-2xx is the server's deliberate answer, and replaying
// a non-idempotent POST on a 5xx can silently double-write.
export async function fetchWithRetry<T>(
  operation: () => Promise<T>,
  options: FetchRetryOptions,
): Promise<T> {
  const limit = options.limit ?? DEFAULT_FETCH_RETRY_LIMIT;
  const baseMs = options.baseMs ?? DEFAULT_FETCH_RETRY_BASE_MS;
  const maxMs = options.maxMs ?? DEFAULT_FETCH_RETRY_MAX_MS;
  const sleep = options.sleep ?? delay;
  const event = options.logEvent ?? 'fetch_retry';

  for (let attempt = 0; attempt <= limit; attempt += 1) {
    const isLast = attempt === limit;
    try {
      return await operation();
    } catch (error) {
      if (!options.isRetryableError(error) || isLast) throw error;
      log.warn(event, {
        attempt: attempt + 1,
        reason: options.describeRetryReason?.(error) ?? 'retryable_error',
        ...options.logFields,
      });
    }
    await sleep(computeFetchBackoffMs(attempt, { baseMs, maxMs }));
  }
  // Loop body always returns or throws on the final iteration.
  throw new Error('fetchWithRetry: unreachable');
}

export function computeFetchBackoffMs(
  attempt: number,
  { baseMs = DEFAULT_FETCH_RETRY_BASE_MS, maxMs = DEFAULT_FETCH_RETRY_MAX_MS } = {},
): number {
  const cap = Math.min(baseMs * 2 ** attempt, maxMs);
  // Equal-jitter backoff: half the capped exponential plus a random half.
  // Avoids the thundering-herd of synchronized retries without ever
  // sleeping near zero on the first retry.
  const half = Math.floor(cap / 2);
  return half + Math.floor(Math.random() * half);
}

// undici surfaces transport-layer failures as either
// `TypeError('fetch failed')` (connect-phase or pre-response error) or
// `TypeError('terminated')` (the WHATWG `terminate` step, raised once the
// response has started streaming and the socket dies). Both expose the
// underlying syscall error on `.cause`.
export function isFetchNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  return error.message === 'fetch failed' || error.message === 'terminated';
}

// Connect-phase errors prove the request never reached the server: TCP
// connect timed out, DNS failed, or the server refused the connection.
// Safe to retry even for non-idempotent methods (POST, PATCH) since no
// side effects can have occurred server-side.
const CONNECT_PHASE_CODES = new Set([
  'UND_ERR_CONNECT_TIMEOUT',
  'ENOTFOUND',
  'EAI_AGAIN',
  'ECONNREFUSED',
]);

// Mid-flight errors: the request was sent and the socket died before (or
// during) the response. The server MAY have processed the request fully
// and we just lost the answer — so these are only safe to retry on
// read-only methods (GET / HEAD) or known-idempotent operations.
// Replaying a POST after one of these can silently double-write unless
// the endpoint is designed to be idempotent.
//
// EPIPE: socket closed unexpectedly during write (Sentry LORE-CLI-23).
// Can occur when the server closes the connection before we finish
// sending the request body, or when a keep-alive socket goes stale.
const MID_FLIGHT_CODES = new Set(['ECONNRESET', 'ETIMEDOUT', 'EPIPE']);

export function isConnectPhaseFetchError(error: unknown): boolean {
  return matchesFetchCauseCode(error, CONNECT_PHASE_CODES);
}

export function isMidFlightFetchError(error: unknown): boolean {
  return matchesFetchCauseCode(error, MID_FLIGHT_CODES);
}

function matchesFetchCauseCode(error: unknown, codes: ReadonlySet<string>): boolean {
  if (!isFetchNetworkError(error)) return false;
  const cause = (error as { cause?: unknown }).cause;
  if (!(cause instanceof Error)) return false;
  const code = (cause as { code?: unknown }).code;
  return typeof code === 'string' && codes.has(code);
}

export function describeFetchError(error: unknown): string {
  if (!isFetchNetworkError(error)) return 'unknown';
  const cause = (error as { cause?: unknown }).cause;
  if (!(cause instanceof Error)) return 'unknown';
  const code = (cause as { code?: unknown }).code;
  return typeof code === 'string' ? `${cause.name}:${code}` : cause.name;
}

type ErrorWithCause = Error & { cause?: unknown; code?: string };

/**
 * Extract structured details from a fetch error for logging. Returns
 * the error message, cause chain messages, and any error codes found.
 */
export function extractFetchErrorDetails(error: unknown): {
  message: string;
  causeChain: string[];
  codes: string[];
} {
  const message = error instanceof Error ? error.message : String(error);
  const causeChain: string[] = [];
  const codes: string[] = [];

  let current: unknown = error instanceof Error ? (error as ErrorWithCause).cause : undefined;
  let depth = 0;
  const maxDepth = 5;

  while (current && depth < maxDepth) {
    if (current instanceof Error) {
      const err = current as ErrorWithCause;
      const code = err.code;
      if (code) codes.push(code);
      causeChain.push(`${err.name}${code ? `[${code}]` : ''}: ${err.message}`);
      current = err.cause;
    } else if (typeof current === 'string') {
      causeChain.push(current);
      break;
    } else {
      causeChain.push(String(current));
      break;
    }
    depth += 1;
  }

  return { message, causeChain, codes };
}
